import { Text } from '@mantine/core'
import { TFunction } from 'i18next'
import { Trans, useTranslation } from 'react-i18next'

const defaultErrorMessage = (t: TFunction, server: string) => (
  <Text>
    <Trans i18nKey="errors.defaultErrorMessage">
      Error connecting to the {{ server }}. Please reload the page and try again. If that does not
      work, file an error report&nbsp;
      <a
        href="https://blocksurvey.io/lamina1-hub-feedback-D.KIfzAfR9.0KNKCBAMGGQ-o"
        target="_blank"
        rel="noreferrer"
      >
        here
      </a>
      &nbsp; or on the&nbsp;
      <a
        href="https://discord.com/channels/981694584474181654/1070486818761474200"
        target="_blank"
        rel="noreferrer"
      >
        LAMINA1 Discord
      </a>
      .
    </Trans>
  </Text>
)

const getErrorMessagesNonMemoized = (t: TFunction) =>
  ({
    hubConnection: t(
      'common.servers.l1Hub',
      'Please connect to the LAMINA1 Hub to continue.'
    ) as string,
    betanetConnection: defaultErrorMessage(t, t('common.servers.l1Betanet', 'LAMINA1 Betanet')),
    idSubnetConnection: defaultErrorMessage(t, t('common.servers.l1Subnet', 'LAMINA1 ID Subnet')),
    idBotConnection: defaultErrorMessage(t, t('common.servers.idBot', 'LAMINA1 ID Bot')),
    studioBotConnection: defaultErrorMessage(
      t,
      t('common.servers.studioBot', 'LAMINA1 Studio Bot')
    ),
    idBotRateLimit: t(
      'errors.idBotRateLimit',
      'Too many change requests. You do not have enough L1 ID tokens to reserve a new username.'
    ),
    switchChains: t(
      'errors.switchChains',
      'You are on the wrong chain. Please switch to the LAMINA1 Betanet or ID Subnet to continue.'
    ),
    metamaskConnection: t(
      'errors.metamaskConnection',
      'You are not connected to Metamask. Please connect to Metamask to continue.'
    ),
    metamaskCancelEvent: t(
      'errors.metamaskCancelEvent',
      'You rejected the Metamask event. Please try again.'
    ),
    idSubnetSendTransaction: t(
      'errors.idSubnetSendTransaction',
      'Error connecting to the L1 Identity Subnet. We can’t verify the username you submitted at this time. Please input the Hub/wallet address you’d like to send to, or try again later.'
    ),
    cmsConnection: defaultErrorMessage(t, t('common.servers.l1CMS', 'LAMINA1 CMS')),
    indexerConnection: defaultErrorMessage(t, t('common.servers.l1Indexer', 'LAMINA1 Indexer')),
    spaceConnection: defaultErrorMessage(t, t('common.servers.l1Space', 'LAMINA1 Space')),
    gameBotConnection: defaultErrorMessage(
      t,
      t('common.servers.l1MvAssetDispenser', 'LAMINA1 Metaverse Asset Dispenser')
    ),
    nftDiscordSharing: defaultErrorMessage(
      t,
      t('common.servers.l1Discord', '#nfts channel on the LAMINA1 Discord')
    ),
  }) as const

export type ErrorMessages = ReturnType<typeof getErrorMessagesNonMemoized>

export const getErrorMessages = (() => {
  const errorMessagesCache = new WeakMap<TFunction, ErrorMessages>()

  return (t: TFunction): ErrorMessages => {
    const memoizedErrorMessages = errorMessagesCache.get(t)
    if (memoizedErrorMessages) {
      return memoizedErrorMessages
    }

    const newErrorMessages = getErrorMessagesNonMemoized(t)
    errorMessagesCache.set(t, newErrorMessages)

    return newErrorMessages
  }
})()

export const useErrorMessages = () => {
  const { t } = useTranslation()
  return getErrorMessages(t)
}

export const globalConstants = {
  CROP_IMAGE_SIZE: {
    width: 512,
    height: 512,
  },
}

const getConstantsNonMemoized = (t: TFunction) =>
  ({
    DISCOVER_HEADER: t('components.discover.header', 'Discover the Open Metaverse'),
  }) as const

export type Constants = ReturnType<typeof getConstantsNonMemoized>

export const getConstants = (() => {
  const constantsCache = new WeakMap<TFunction, Constants>()

  return (t: TFunction): Constants => {
    const memoizedConstants = constantsCache.get(t)
    if (memoizedConstants) {
      return memoizedConstants
    }

    const newConstants = getConstantsNonMemoized(t)
    constantsCache.set(t, newConstants)

    return newConstants
  }
})()

export const useConstants = () => {
  const { t } = useTranslation()
  return getConstants(t)
}
